/**
 * Drag-drop + browse attachment picker, uploading each file in parts as soon as it is picked (ADR-0003). The picked
 * set lives in the parent's `useAttachmentUploads` (so the launch can wait for it and send its `attachments`); this
 * renders it: per-file progress, a retry that resumes a failed upload, remove (which abandons it), image previews from
 * the picked `File`. Limits are the server's (`useUploadLimits`); a file is never read whole here.
 *
 * Mobile-friendly: the dropzone is also a tap target that opens the native
 * file browser; the file list wraps and each chip has a large remove hit area.
 */
import { Icon, iconForMime, type UploadLimitViolation, type UseAttachmentUploads } from '@evt/flow-stream-ui'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useI18n } from '../i18n'
import { codeMessage } from '../i18n/errors'

/** The slice of a `DataTransferItem` a drop is read through (and the test seam). */
export interface DroppedItem {
  kind: string
  getAsFile(): File | null
  webkitGetAsEntry?: () => { isDirectory: boolean } | null
}

/**
 * The files of a drop, and how many folders it held. A folder is refused only when the browser says it is one
 * (`webkitGetAsEntry().isDirectory`): an empty file with no type — a `.env`, an empty `.txt` — is a file and is kept.
 * Read during the drop event: a `DataTransfer` is empty once its handler returns.
 */
export function splitDropped(items: ArrayLike<DroppedItem>): { files: File[]; folders: number } {
  const files: File[] = []
  let folders = 0
  for (const item of Array.from(items)) {
    if (item.kind !== 'file') continue
    if (item.webkitGetAsEntry?.()?.isDirectory) {
      folders++
      continue
    }
    const file = item.getAsFile()
    if (file) files.push(file)
  }
  return { files, folders }
}

export function AttachmentPicker({
  uploads,
  limits,
  disabled
}: {
  uploads: UseAttachmentUploads
  limits: { maxTotalBytes: number } | null
  disabled?: boolean
}) {
  const { t, formatBytes } = useI18n()
  const [error, setError] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const dragDepth = useRef(0)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const files = uploads.items
  const totalRaw = uploads.totalBytes
  const cap = limits?.maxTotalBytes ?? null
  const nearCap = cap !== null && totalRaw > cap * 0.9

  // Image thumbnails — object URLs of the picked files, revoked when the set changes.
  const thumbs = useMemo(() => {
    const map = new Map<string, string>()
    for (const f of files) {
      if (f.mimetype.startsWith('image/')) map.set(f.id, URL.createObjectURL(f.file))
    }
    return map
  }, [files])
  useEffect(() => {
    return () => {
      for (const url of thumbs.values()) URL.revokeObjectURL(url)
    }
  }, [thumbs])

  const describe = (v: UploadLimitViolation): string => {
    if (v.kind === 'file_too_large') return t.newRun.attachPerFileExceeded(v.name, formatBytes(v.limit))
    if (v.kind === 'total_too_large') return t.newRun.attachTotalExceeded(formatBytes(v.total), formatBytes(v.limit))
    return t.newRun.attachTooMany(v.count, v.limit)
  }

  const handleFiles = (picked: ReadonlyArray<File>, folders = 0) => {
    setError(folders > 0 ? t.newRun.attachFolderUnsupported : null)
    if (picked.length === 0) return
    const violation = uploads.add(picked)
    if (violation) setError(describe(violation))
  }

  // A failed upload says why in our words: the server's message is written for an operator.
  const failedTitle = (code: string | undefined): string => codeMessage(t, code) ?? t.newRun.attachFailed

  const removeFile = (id: string) => {
    uploads.remove(id)
    setError(null)
  }

  const openPicker = () => {
    if (disabled) return
    inputRef.current?.click()
  }

  return (
    <div className="col gap-2 attach-picker">
      <div className="row gap-2">
        <div className="field-label">{t.newRun.attachTitle}</div>
        <span
          className="attach-meter"
          style={{ marginLeft: 'auto', color: nearCap ? 'var(--danger)' : 'var(--ink-3)' }}
          aria-live="polite"
        >
          {cap === null
            ? files.length === 0
              ? ''
              : t.newRun.attachMeterUsed(files.length, formatBytes(totalRaw), '…')
            : files.length === 0
              ? t.newRun.attachMeterEmpty(formatBytes(cap))
              : t.newRun.attachMeterUsed(files.length, formatBytes(totalRaw), formatBytes(cap))}
        </span>
      </div>

      <button
        type="button"
        className={`pg-dropzone${isDragging ? ' active' : ''}`}
        disabled={disabled}
        aria-label={t.newRun.attachDropzoneAria}
        onClick={openPicker}
        onDragEnter={(e) => {
          e.preventDefault()
          if (disabled) return
          dragDepth.current += 1
          if (dragDepth.current === 1) setIsDragging(true)
        }}
        onDragLeave={(e) => {
          e.preventDefault()
          dragDepth.current = Math.max(0, dragDepth.current - 1)
          if (dragDepth.current === 0) setIsDragging(false)
        }}
        onDragOver={(e) => {
          e.preventDefault()
          e.dataTransfer.dropEffect = disabled ? 'none' : 'copy'
        }}
        onDrop={(e) => {
          e.preventDefault()
          dragDepth.current = 0
          setIsDragging(false)
          if (disabled) return
          // `items` says which entries are folders; a browser without it hands over `files` as they are.
          if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
            const { files: dropped, folders } = splitDropped(e.dataTransfer.items)
            handleFiles(dropped, folders)
          } else handleFiles(Array.from(e.dataTransfer.files))
        }}
      >
        <Icon name="upload" size={14} />
        <span>{cap === null ? t.newRun.attachDropzoneHintPlain : t.newRun.attachDropzoneHint(formatBytes(cap))}</span>
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          handleFiles(Array.from(e.target.files ?? []))
          e.target.value = ''
        }}
      />

      {files.length > 0 && (
        <div className="chips">
          {files.map((f) => {
            const thumb = thumbs.get(f.id)
            const percent = f.size === 0 ? 100 : Math.floor((f.sent / f.size) * 100)
            return (
              <span
                key={f.id}
                className={`chip upload-chip upload-${f.state}`}
                title={f.state === 'failed' ? failedTitle(f.errorCode) : `${f.mimetype} · ${formatBytes(f.size)}`}
              >
                {thumb ? <img src={thumb} alt="" /> : <Icon name={iconForMime(f.mimetype)} size={12} />}
                <span className="attach-name">{f.name}</span>
                <span className="muted">{formatBytes(f.size)}</span>
                {(f.state === 'queued' || f.state === 'uploading') && (
                  <span
                    className="upload-progress"
                    role="progressbar"
                    aria-label={t.newRun.attachUploading(f.name)}
                    aria-valuenow={percent}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  >
                    {percent}%
                  </span>
                )}
                {f.state === 'failed' && (
                  <button
                    type="button"
                    className="x retry"
                    aria-label={t.newRun.attachRetry(f.name)}
                    title={failedTitle(f.errorCode)}
                    onClick={() => uploads.retry(f.id)}
                  >
                    <Icon name="refresh" size={12} />
                  </button>
                )}
                <button
                  type="button"
                  className="x"
                  aria-label={t.newRun.attachRemove(f.name)}
                  title={t.newRun.attachRemove(f.name)}
                  onClick={() => removeFile(f.id)}
                >
                  <Icon name="x" size={12} />
                </button>
              </span>
            )
          })}
        </div>
      )}

      {files.some((f) => f.state === 'failed') && <div className="field-error">{t.newRun.attachFailed}</div>}
      {error && <div className="field-error">{error}</div>}
    </div>
  )
}
